import {
  encodeKamikaze,
  FRAME_EVERY,
  GuidedFlight,
  KamikazeFlight,
  BomberFlight,
  predictBombImpact,
  SIM_DT,
  WIND_ACCEL,
  WATER_Y,
  type KamikazeInput,
  type TurnInput,
  type Terrain,
} from '@tanky/shared';

type Dir = -1 | 0 | 1;

/**
 * Živé řízení řízené střely v prohlížeči. Každý snímek posune sdílenou
 * simulaci (GuidedFlight) o uplynulé kroky s aktuálním směrem a zaznamená
 * řízení – server pak let přepočítá ze stejného záznamu.
 */
export class GuidedPilot {
  private acc = 0;
  private keyDir: Dir = 0;
  private touchDir: Dir = 0;
  private runs: [number, Dir][] = [];
  /** Body dráhy po 1/60 s (pro stopu). */
  readonly pts: number[] = [];

  constructor(readonly flight: GuidedFlight) {
    this.pts.push(flight.x, flight.y);
  }

  get done() {
    return this.flight.status !== 'flying';
  }

  get dir(): Dir {
    return this.touchDir || this.keyDir;
  }

  setKeyDir(d: Dir) {
    this.keyDir = d;
  }

  setTouchDir(d: Dir) {
    this.touchDir = d;
  }

  /** Posune let o reálný čas dt. */
  update(dt: number) {
    this.acc += dt;
    const dir = this.dir;
    while (this.acc >= SIM_DT && !this.done) {
      this.acc -= SIM_DT;
      this.flight.step(dir);
      const last = this.runs[this.runs.length - 1];
      if (last && last[1] === dir) last[0]++;
      else this.runs.push([1, dir]);
      if (this.flight.steps % FRAME_EVERY === 0) this.pts.push(this.flight.x, this.flight.y);
    }
  }

  get steer(): NonNullable<TurnInput['steer']> {
    return this.runs.map(([n, d]) => [n, d]);
  }
}

/**
 * Živé řízení kamikadze dronu: stejné osy jako průzkumný dron (plyn, otočení,
 * dopředu, úkrok), odpálení na povel. Záznam povelů se pošle serveru, ten let
 * přepočítá stejnou simulací (KamikazeFlight); konec záznamu = výbuch.
 */
export class KamikazePilot {
  private acc = 0;
  private runs: [number, number][] = [];
  private detonateReq = false;
  readonly pts: number[] = [];
  /** Aktuální vstup (nastavuje GameScreen z kláves a páček). */
  readonly input: KamikazeInput = { throttle: 0, yaw: 0, forward: 0, strafe: 0 };
  /** Náklon kamery (rad) – jen vizuální, jako u průzkumného dronu. */
  pitch = 0;
  roll = 0;

  constructor(readonly flight: KamikazeFlight) {
    this.pts.push(flight.x, flight.y);
  }

  get done() {
    return this.flight.status !== 'flying';
  }

  detonate() {
    this.detonateReq = true;
  }

  update(dt: number) {
    this.acc += dt;
    const code = encodeKamikaze(this.input);
    while (this.acc >= SIM_DT && !this.done) {
      this.acc -= SIM_DT;
      if (this.detonateReq) {
        this.flight.detonate();
        break;
      }
      this.flight.step(code);
      const last = this.runs[this.runs.length - 1];
      if (last && last[1] === code) last[0]++;
      else this.runs.push([1, code]);
      if (this.flight.steps % FRAME_EVERY === 0) this.pts.push(this.flight.x, this.flight.y);
    }
    const inp = this.input;
    const k = 1 - Math.exp(-dt * 3);
    this.pitch += (-inp.forward * 0.18 - 0.12 - this.pitch) * k;
    this.roll += (inp.strafe * 0.25 + inp.yaw * 0.1 - this.roll) * k;
  }

  get steer(): NonNullable<TurnInput['steer']> {
    return this.runs.map(([n, d]) => [n, d]);
  }
}

export interface ActiveBomb {
  x: number;
  y: number;
  vx: number;
  vy: number;
  trail: number[];
  alive: boolean;
}

/**
 * Řízení bombometčíka v bombardéru:
 * Letadlo letí horizontálně nad mapou, bombometčík vidí zaměřovač promítnutý na zem
 * a stisknutím mezerníku / tlačítka shazuje bomby.
 */
export class BomberPilot {
  private acc = 0;
  private runs: [number, number][] = [];
  private dropRequested = false;
  readonly pts: number[] = [];
  readonly bombs: ActiveBomb[] = [];

  constructor(
    readonly flight: BomberFlight,
    private readonly terrain: Terrain,
    private readonly wind: number,
  ) {
    this.pts.push(flight.x, flight.y);
  }

  get done() {
    return (
      (this.flight.status !== 'flying' || (this.flight.bombsLeft === 0 && this.bombs.length > 0)) &&
      this.bombs.every((b) => !b.alive)
    );
  }

  dropBomb() {
    this.dropRequested = true;
  }

  setDrop(down: boolean) {
    this.dropRequested = down;
  }

  /** Získá předpovězený dopad bomby pro zaměřovač */
  get crosshair(): { x: number; y: number } {
    return predictBombImpact(
      this.terrain,
      this.flight.x,
      this.flight.y + 16,
      this.flight.vx * 0.85,
      45,
      this.wind,
    );
  }

  update(dt: number, onDrop?: (x: number, y: number) => void) {
    this.acc += dt;
    while (this.acc >= SIM_DT && !this.flightDone) {
      this.acc -= SIM_DT;
      const code = this.dropRequested && this.flight.bombsLeft > 0 ? 1 : 0;
      const dropped = this.flight.step(code);
      if (dropped) {
        const last = this.flight.droppedBombs[this.flight.droppedBombs.length - 1];
        this.bombs.push({
          x: last.x,
          y: last.y,
          vx: last.vx,
          vy: last.vy,
          trail: [last.x, last.y],
          alive: true,
        });
        onDrop?.(last.x, last.y);
      }
      const lastRun = this.runs[this.runs.length - 1];
      if (lastRun && lastRun[1] === code) lastRun[0]++;
      else this.runs.push([1, code]);

      if (this.flight.steps % FRAME_EVERY === 0) this.pts.push(this.flight.x, this.flight.y);

      // Posun shozených bomb
      for (const b of this.bombs) {
        if (!b.alive) continue;
        b.vx += this.wind * WIND_ACCEL * SIM_DT;
        b.vy += this.terrain.gravity * SIM_DT;
        b.x += b.vx * SIM_DT;
        b.y += b.vy * SIM_DT;
        b.trail.push(b.x, b.y);
        if (b.y >= WATER_Y || this.terrain.solid(b.x, b.y)) {
          b.alive = false;
        }
      }
    }

    if (this.flightDone) {
      while (this.acc >= SIM_DT && this.bombs.some((b) => b.alive)) {
        this.acc -= SIM_DT;
        for (const b of this.bombs) {
          if (!b.alive) continue;
          b.vx += this.wind * WIND_ACCEL * SIM_DT;
          b.vy += this.terrain.gravity * SIM_DT;
          b.x += b.vx * SIM_DT;
          b.y += b.vy * SIM_DT;
          b.trail.push(b.x, b.y);
          if (b.y >= WATER_Y || this.terrain.solid(b.x, b.y)) {
            b.alive = false;
          }
        }
      }
    }
  }

  get flightDone() {
    return this.flight.status !== 'flying';
  }

  get steer(): NonNullable<TurnInput['steer']> {
    return this.runs.map(([n, d]) => [n, d]);
  }
}
